import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { WeekSummaryDTO } from '@pickem/shared';
import { useApi } from '../../lib/useApi';
import { Spinner, ErrorBanner, Card, Field, SectionTitle } from '../../components/ui';
import { api, ApiError } from '../../lib/api';
import { formatDateTime, weekStateLabel } from '../../lib/format';

interface WeeksData {
  season?: { id: string; year: number };
  weeks: (WeekSummaryDTO & { submissions: number; hasToken: boolean })[];
}

export function AdminWeeksPage() {
  const { data, error, loading, reload } = useApi<WeeksData>('/admin/weeks');
  const [weekNumber, setWeekNumber] = useState('');
  const [createError, setCreateError] = useState('');
  const [busy, setBusy] = useState(false);

  async function createWeek(e: React.FormEvent) {
    e.preventDefault();
    setCreateError('');
    setBusy(true);
    try {
      await api.post('/admin/weeks', { weekNumber: Number(weekNumber) });
      setWeekNumber('');
      reload();
    } catch (err) {
      setCreateError(err instanceof ApiError ? err.message : 'Unable to create week.');
    } finally {
      setBusy(false);
    }
  }

  if (loading) return <Spinner />;
  if (error) return <ErrorBanner message={error} />;

  return (
    <div className="space-y-6">
      <SectionTitle>Weeks</SectionTitle>

      <Card>
        <form onSubmit={createWeek} className="flex flex-wrap items-end gap-3">
          <div className="w-32">
            <Field label="New week #" htmlFor="weekNumber">
              <input
                id="weekNumber"
                type="number"
                min={1}
                className="input"
                value={weekNumber}
                onChange={(e) => setWeekNumber(e.target.value)}
                required
              />
            </Field>
          </div>
          <button type="submit" className="btn-primary" disabled={busy || !weekNumber}>
            {busy ? 'Creating…' : 'Create week'}
          </button>
          {createError && <span className="text-sm text-danger">{createError}</span>}
        </form>
        <p className="mt-1 text-xs text-muted">
          After creating, open the week to sync the NFL schedule and generate the QR code.
        </p>
      </Card>

      {data && data.weeks.length > 0 ? (
        <div className="space-y-2">
          {[...data.weeks].sort((a, b) => b.weekNumber - a.weekNumber).map((w) => (
            <Link
              key={w.id}
              to={`/admin/weeks/${w.id}`}
              className="card flex items-center justify-between px-4 py-3 transition-colors hover:bg-cream"
            >
              <div>
                <div className="font-display text-2xl uppercase text-grove">{w.displayName}</div>
                <div className="text-sm text-muted">
                  Locks {formatDateTime(w.locksAt)} · {w.submissions} submissions
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="chip bg-grove/10 text-grove">{weekStateLabel(w.state)}</span>
                {w.hasToken && <span className="chip bg-amber/20 text-amber-dark">QR</span>}
              </div>
            </Link>
          ))}
        </div>
      ) : (
        <p className="text-sm text-muted">No weeks yet. Create one above.</p>
      )}
    </div>
  );
}
